import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Key, Plus, Copy, Trash2, AlertTriangle, Check } from "lucide-react";

interface ApiKey {
  id: string;
  name: string;
  description: string | null;
  key_prefix: string;
  scopes: string[];
  is_active: boolean;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
  revoked_at: string | null;
}

const scopeLabels: Record<string, string> = {
  "salescompass:read": "Sales Compass (Lesen)",
};

export default function ApiKeyManagement() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Create form
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [expiresInDays, setExpiresInDays] = useState<number | "">("");

  // Newly created key (shown once)
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("manage-api-keys", {
      method: "GET",
    });
    if (!error && data?.keys) {
      setKeys(data.keys);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }

    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-api-keys", {
        method: "POST",
        body: {
          name: name.trim(),
          description: description.trim() || undefined,
          scopes: ["salescompass:read"],
          expires_in_days: expiresInDays || undefined,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setNewKey(data.api_key);
      setName("");
      setDescription("");
      setExpiresInDays("");
      setShowCreate(false);
      fetchKeys();
      toast.success("API-Schlüssel erstellt");
    } catch (err: any) {
      toast.error(err.message || "Fehler beim Erstellen");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId: string) => {
    setRevokingId(keyId);
    try {
      const { data, error } = await supabase.functions.invoke(
        `manage-api-keys?id=${keyId}`,
        { method: "DELETE" }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("API-Schlüssel widerrufen");
      fetchKeys();
    } catch (err: any) {
      toast.error(err.message || "Fehler");
    } finally {
      setRevokingId(null);
    }
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputClass =
    "w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary transition-colors";

  return (
    <div className="space-y-6">
      <div className="p-6 border border-border bg-card space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-subheadline mb-1">API-Schlüssel</h3>
            <p className="text-caption">
              Verwalten Sie API-Schlüssel für die externe Anbindung (z.B. Sales Compass)
            </p>
          </div>
          <Button
            variant="commit"
            onClick={() => {
              setShowCreate(!showCreate);
              setNewKey(null);
            }}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Neuer API-Schlüssel
          </Button>
        </div>

        {/* Newly created key banner */}
        {newKey && (
          <div className="p-5 border border-accent bg-accent/5 space-y-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
              <div className="space-y-2 flex-1">
                <p className="text-body font-medium">
                  API-Schlüssel erstellt – jetzt kopieren!
                </p>
                <p className="text-caption">
                  Dieser Schlüssel wird nur einmal angezeigt. Speichern Sie ihn jetzt sicher ab.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-background border border-border text-sm font-mono break-all select-all">
                    {newKey}
                  </code>
                  <Button
                    variant="quiet"
                    size="sm"
                    onClick={() => copyToClipboard(newKey)}
                    className="gap-1.5 shrink-0"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3.5 h-3.5" /> Kopiert
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" /> Kopieren
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => setNewKey(null)}>
                Verstanden, schließen
              </Button>
            </div>
          </div>
        )}

        {/* Create form */}
        {showCreate && (
          <div className="p-5 border border-accent/20 bg-accent/5 space-y-4">
            <h4 className="text-body font-medium">Neuen API-Schlüssel erstellen</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-micro block">Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="z.B. Sales Compass Produktion"
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <label className="text-micro block">Gültigkeit (Tage, leer = unbegrenzt)</label>
                <input
                  type="number"
                  value={expiresInDays}
                  onChange={(e) =>
                    setExpiresInDays(e.target.value ? parseInt(e.target.value) : "")
                  }
                  placeholder="z.B. 365"
                  className={inputClass}
                  min={1}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-micro block">Beschreibung (optional)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Wofür wird dieser Schlüssel verwendet?"
                className={inputClass}
              />
            </div>

            <div className="space-y-2">
              <label className="text-micro block">Berechtigungen</label>
              <div className="flex gap-2">
                <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-accent bg-accent/10 text-accent">
                  <Key className="w-3 h-3" />
                  Sales Compass (Lesen)
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                API-Schlüssel haben ausschließlich Lesezugriff auf die Sales Compass Endpunkte.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="commit" onClick={handleCreate} disabled={creating}>
                {creating ? "Wird erstellt..." : "Schlüssel generieren"}
              </Button>
              <Button variant="ghost" onClick={() => setShowCreate(false)}>
                Abbrechen
              </Button>
            </div>
          </div>
        )}

        {/* Keys list */}
        <div className="divide-y divide-border border border-border">
          {loading ? (
            <div className="p-6 text-center text-caption">Wird geladen...</div>
          ) : keys.length === 0 ? (
            <div className="p-6 text-center text-caption">
              Keine API-Schlüssel vorhanden
            </div>
          ) : (
            keys.map((k) => (
              <div
                key={k.id}
                className={cn(
                  "flex items-center justify-between p-4 transition-colors",
                  k.is_active
                    ? "hover:bg-secondary/20"
                    : "opacity-50 bg-secondary/10"
                )}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={cn(
                      "w-8 h-8 flex items-center justify-center shrink-0 border",
                      k.is_active
                        ? "border-accent/30 bg-accent/5 text-accent"
                        : "border-border bg-secondary text-muted-foreground"
                    )}
                  >
                    <Key className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-foreground font-medium truncate">
                        {k.name}
                      </p>
                      <code className="text-xs text-muted-foreground font-mono">
                        {k.key_prefix}…
                      </code>
                      {!k.is_active && (
                        <span className="text-xs px-1.5 py-0.5 bg-destructive/10 text-destructive border border-destructive/20">
                          Widerrufen
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      <span>
                        Erstellt:{" "}
                        {new Date(k.created_at).toLocaleDateString("de-DE")}
                      </span>
                      {k.last_used_at && (
                        <span>
                          Zuletzt:{" "}
                          {new Date(k.last_used_at).toLocaleDateString("de-DE")}
                        </span>
                      )}
                      {k.expires_at && (
                        <span>
                          Läuft ab:{" "}
                          {new Date(k.expires_at).toLocaleDateString("de-DE")}
                        </span>
                      )}
                      {k.scopes.map((s) => (
                        <span
                          key={s}
                          className="px-1.5 py-0.5 bg-secondary border border-border text-[10px]"
                        >
                          {scopeLabels[s] || s}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                {k.is_active && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevoke(k.id)}
                    disabled={revokingId === k.id}
                    className="text-destructive hover:text-destructive shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    {revokingId === k.id ? "..." : "Widerrufen"}
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
